import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { isEqual } from 'lodash-es';
import { useAppSelector } from 'hooks/useAppSelector';
import { DepositOptions, DepositRadioOptionType } from './types';
import { parseStringToNumber } from 'utils/numbers';
import { DefaultErrorState } from 'components/common/Payable';
import { ErrorState } from 'types/Payable';
import { Deposit, DepositValueType } from '@urbanx/agx.campaigns.client';
import {
  AgxDropdown,
  AgxRadioGroup,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
  Icon_AlertCircle,
} from '@urbanx/agx.ui-components2';
import './Deposit.scss';

export const MaxDepositPercentage = 0.1;
export const ErrorMessage = `Deposit can't exceed ${MaxDepositPercentage * 100}% of the sale price`;
const DepositPercentRequiredMessage = 'Enter deposit percentage';
const DepositAmountRequiredMessage = 'Enter deposit amount';

export const CalculateAmount = (salePrice: number, deposit: Deposit) => {
  const amount = deposit?.amount ? Number(deposit.amount) : 0;
  const percent = deposit?.percent ? Number(deposit.percent) : 0;

  return deposit?.depositValueType === DepositValueType.Percentage
    ? salePrice * (percent / 100)
    : amount;
};

const customOption = 'customAmount';

const radioOptions = [
  {
    label: `${DepositRadioOptionType.TenPercentage}%`,
    value: DepositRadioOptionType.TenPercentage,
  },
  {
    label: `${DepositRadioOptionType.FivePercentage}%`,
    value: DepositRadioOptionType.FivePercentage,
  },
  {
    label: '$__',
    value: customOption,
  },
];

interface DepositProps {
  id: string;
  label: string;
  onValueChanged: (change: { id: string; value: Deposit }) => void;
  defaultValue: Deposit;
  required?: boolean;
  validate?: boolean;
  balanceDepositValidation?: boolean;
  error?: ErrorState;
  showDepositDropdownOption?: boolean;
}

const getNormalizedDepositValue = (
  defaultValue: Deposit,
  showDepositDropdownOption: boolean
): Deposit => ({
  depositType: defaultValue?.depositType,
  depositValueType:
    defaultValue?.depositValueType ?? DepositValueType.Percentage,
  amount: defaultValue?.amount,
  isDepositSelected: defaultValue?.isDepositSelected ?? false,
  conditionType: defaultValue?.conditionType,
  percent: showDepositDropdownOption
    ? defaultValue?.percent
    : (defaultValue?.percent ?? parseInt(DepositRadioOptionType.TenPercentage)),
});

const DepositComponent: React.FC<DepositProps> = ({
  id,
  label,
  onValueChanged,
  defaultValue,
  required = false,
  balanceDepositValidation = false,
  error = DefaultErrorState,
  showDepositDropdownOption = true,
  validate = false,
}: DepositProps) => {
  const { SalePrice } = useAppSelector(state => state.form.formValues);
  const normalizedDefaultValue = useMemo(
    () => getNormalizedDepositValue(defaultValue, showDepositDropdownOption),
    [defaultValue, showDepositDropdownOption]
  );

  const [depositValue, setDepositValue] = useState<Deposit>(
    normalizedDefaultValue
  );

  const validateDepositTotal = useCallback((): ErrorState => {
    const amount = CalculateAmount(SalePrice, depositValue);
    const isInitialDepositInvalid = amount > SalePrice * MaxDepositPercentage;

    return {
      depositError: isInitialDepositInvalid,
      message: isInitialDepositInvalid ? ErrorMessage : undefined,
    };
  }, [SalePrice, depositValue]);

  const errorState = useMemo(() => {
    const localTotalError = validateDepositTotal();
    return localTotalError.depositError
      ? localTotalError
      : error?.depositError
        ? error
        : DefaultErrorState;
  }, [error, validateDepositTotal]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    const shouldEmitInitialValue = isInitialMount.current;
    isInitialMount.current = false;

    if (!shouldEmitInitialValue && isEqual(normalizedDefaultValue, depositValue)) {
      return;
    }

    onValueChanged({
      id,
      value: { ...depositValue },
    });
  }, [depositValue, id, normalizedDefaultValue, onValueChanged]);

  const handleDepositValueTypeChange = useCallback((value: string) => {
    setDepositValue(prev => ({
      ...prev,
      depositValueType: value as DepositValueType,
      amount: value === DepositValueType.Dollar ? prev?.amount : undefined,
      percent:
        value === DepositValueType.Percentage ? prev?.percent : undefined,
    }));
  }, []);

  const onPercentChanged = useCallback((value: string) => {
    const parsedValue = parseStringToNumber(value);

    setDepositValue(prev => ({
      ...prev,
      percent: parsedValue,
      amount: undefined,
    }));
  }, []);

  const onDollarChanged = useCallback((value: string) => {
    const parsedValue = parseStringToNumber(value);

    setDepositValue(prev => ({
      ...prev,
      amount: parsedValue,
      percent: undefined,
    }));
  }, []);

  const onCheckChanged = useCallback((value: string) => {
    if (value === DepositRadioOptionType.TenPercentage) {
      setDepositValue(prev => ({
        ...prev,
        depositValueType: DepositValueType.Percentage,
        percent: parseInt(DepositRadioOptionType.TenPercentage),
        amount: undefined,
      }));
    } else if (value === DepositRadioOptionType.FivePercentage) {
      setDepositValue(prev => ({
        ...prev,
        depositValueType: DepositValueType.Percentage,
        percent: parseInt(DepositRadioOptionType.FivePercentage),
        amount: undefined,
      }));
    } else {
      setDepositValue(prev => ({
        ...prev,
        depositValueType: DepositValueType.Dollar,
      }));
    }
  }, []);

  const selectedRadioOption = useMemo(() => {
    if (
      depositValue.depositValueType === DepositValueType.Percentage &&
      depositValue.percent?.toString() === DepositRadioOptionType.TenPercentage
    ) {
      return DepositRadioOptionType.TenPercentage;
    } else if (
      depositValue.depositValueType === DepositValueType.Percentage &&
      depositValue.percent?.toString() === DepositRadioOptionType.FivePercentage
    ) {
      return DepositRadioOptionType.FivePercentage;
    } else if (depositValue.depositValueType === DepositValueType.Dollar) {
      return customOption;
    } else {
      return '';
    }
  }, [depositValue]);

  const onCustomAmountChanged = useCallback((value: string) => {
    const parsedValue = parseStringToNumber(value);

    setDepositValue(prev => ({
      ...prev,
      amount: parsedValue,
      percent: undefined,
    }));
  }, []);

  const depositInputErrorMessage = useMemo(() => {
    if (errorState.depositError) {
      return errorState.message;
    }

    if (!validate) {
      return undefined;
    }

    if (depositValue?.depositValueType === DepositValueType.Dollar) {
      return depositValue?.amount === undefined
        ? DepositAmountRequiredMessage
        : undefined;
    }

    return depositValue?.percent === undefined
      ? DepositPercentRequiredMessage
      : undefined;
  }, [
    depositValue?.amount,
    depositValue?.depositValueType,
    depositValue?.percent,
    errorState,
    validate,
  ]);

  return (
    <>
      {showDepositDropdownOption && (
        <AgxSpacer direction="vertical" gap="spacing-lg" fill>
          {label && (
            <AgxTypography
              id={`label-${id}`}
              type="label"
              size="medium"
              color="var(--text-primary)"
            >
              {label}
            </AgxTypography>
          )}
          <AgxSpacer direction="horizontal" gap="xs" contentAlignment="start">
            <div className="depositTypeDropdown">
              <AgxDropdown
                options={DepositOptions}
                id={`select-${id}`}
                selected={depositValue?.depositValueType}
                onChange={handleDepositValueTypeChange}
                noHeader
                required
              />
            </div>
            {depositValue?.depositValueType === DepositValueType.Percentage && (
              <AgxTextInput
                id={`percent-${id}`}
                type="percent"
                defaultValue={depositValue?.percent?.toString() || ''}
                onChange={onPercentChanged}
                stretch
                errorText={depositInputErrorMessage}
                hideErrorDescription
                required={required || balanceDepositValidation}
                noHeader
              />
            )}
            {depositValue?.depositValueType === DepositValueType.Dollar && (
              <AgxTextInput
                id={`dollar-${id}`}
                type="currency"
                defaultValue={depositValue?.amount?.toString() || ''}
                onChange={onDollarChanged}
                stretch
                errorText={depositInputErrorMessage}
                hideErrorDescription
                required={required || balanceDepositValidation}
                noHeader
                addTrailingZeros={false}
              />
            )}
          </AgxSpacer>
          {depositInputErrorMessage && (
            <AgxSpacer direction="horizontal" gap={'3xs'} fill>
              <Icon_AlertCircle type={'danger'} height={16} width={16} />
              <AgxTypography
                id={'txtDepositError'}
                type={'body_ui'}
                size={'small'}
                color="var(--text-error)"
              >
                {depositInputErrorMessage}
              </AgxTypography>
            </AgxSpacer>
          )}
        </AgxSpacer>
      )}
      {!showDepositDropdownOption && (
        <AgxRadioGroup
          id={`deposit-radio-group-${id}`}
          layout={'vertical'}
          label={label}
          options={radioOptions}
          value={selectedRadioOption}
          onChange={onCheckChanged}
          subContent={
            depositValue.depositValueType === DepositValueType.Dollar ? (
              <AgxSpacer
                direction="horizontal"
                contentAlignment="center"
                padding="md"
                key="customAmountInput"
                fill
              >
                <AgxTextInput
                  id={`dollar-${id}`}
                  type="currency"
                  defaultValue={depositValue?.amount?.toString() || ''}
                  onChange={onCustomAmountChanged}
                  stretch
                  noHeader
                  errorText={
                    errorState.depositError
                      ? errorState.message
                      : validate && depositValue?.amount === undefined
                        ? DepositAmountRequiredMessage
                        : undefined
                  }
                  required={required}
                  addTrailingZeros={false}
                />
              </AgxSpacer>
            ) : undefined
          }
        />
      )}
    </>
  );
};

export default DepositComponent;
